import { useState, useEffect, useCallback, useMemo } from 'react';
import { useCentreSearchService } from '../contexts/CentreSearchServiceContext';
import useRoles from '../../../hooks/useRoles';
import { Eventbus } from '../../../utils';
import events from '../../../utils/constants/events';

export function centreSearchFilterOption(option, rawInput) {
  if (!rawInput) return true;
  const words = rawInput.toLowerCase().split(' ').filter(Boolean);
  const item = option.data;
  const haystack = [
    item.centreName,
    item.centreDisplayName,
    item.centreAreaName,
    item.centreRegionName,
    item.centreBrandName,
    ...(item.licenceNames ?? []),
    item.buildingTypeName,
    item.propertyName,
    item.propertyStatusCode,
    item.address?.address1,
    item.address?.address2,
    item.address?.address3,
    item.address?.city,
    item.address?.postalCode,
    item.centreStaff?.map((s) => s.staffName).join(' '),
    ...(item.centreTags ?? []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return words.every((w) => haystack.includes(w));
}

export default function useCentreSearch() {
  const service = useCentreSearchService();
  const { hasPropertyAccess } = useRoles();

  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadProperties = useCallback(async () => {
    setLoading(true);
    const data = await service.loadProperties();
    setCentres(data);
    setLoading(false);
  }, [service]);

  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  // Live sync: merge SignalR messages into the denormalised list (no API reload).
  useEffect(() => {
    const E = events.SIGNALR;

    const mergeDefined = (base, patch) => {
      const out = { ...base };
      for (const [k, v] of Object.entries(patch)) {
        if (v != null && !(Array.isArray(v) && v.length === 0)) out[k] = v;
      }
      return out;
    };

    const onCentre = (msg) => {
      const id = msg?.centreId;
      if (id == null) return;
      const patch = {
        centreName: msg.centreName,
        centreDisplayName: msg.displayName ?? msg.centreDisplayName,
        centreAreaName: msg.area?.areaName,
        centreRegionName: msg.area?.regionName,
        centreBrandName: msg.brand?.brandName,
        currentStatus: msg.centreStatus?.statusDescription ?? msg.currentStatus,
        centreTags: msg.centreTags,
      };
      setCentres((prev) => {
        let changed = false;
        const next = prev.map((row) => {
          if (row.centreId !== id) return row;
          changed = true;
          return mergeDefined(row, patch);
        });
        return changed ? next : prev;
      });
    };

    const onProperty = (msg) => {
      const pid = msg?.propertyId;
      if (pid == null) return;
      setCentres((prev) => {
        const idx = prev.findIndex((r) => r.propertyId === pid);
        if (idx === -1) return prev; // new rows need denormalised data we don't have
        const next = prev.slice();
        next[idx] = mergeDefined(prev[idx], msg);
        return next;
      });
    };

    const onStaff = (msg) => {
      const rows = Array.isArray(msg) ? msg : [msg];
      setCentres((prev) => {
        let changed = false;
        const next = prev.map((row) => {
          const relevant = rows.filter((r) => r?.staffId != null && r?.parentId === row.centreId);
          if (relevant.length === 0) return row;
          changed = true;
          const staff = [...(row.centreStaff ?? [])];
          relevant.forEach((r) => {
            const staffName = [r.firstName, r.lastName].filter(Boolean).join(' ') || r.staffName;
            const i = staff.findIndex((s) => s.staffId === r.staffId);
            const entry = { ...(i >= 0 ? staff[i] : {}), staffId: r.staffId, staffName };
            if (i >= 0) staff[i] = entry; else staff.push(entry);
          });
          return { ...row, centreStaff: staff };
        });
        return changed ? next : prev;
      });
    };

    const onCentreDeleted = (msg) => {
      const id = msg && typeof msg === 'object' ? msg.centreId : msg;
      if (id == null) return;
      setCentres((prev) => prev.filter((r) => r.centreId !== id));
    };
    const onPropertyDeleted = (msg) => {
      const id = msg && typeof msg === 'object' ? msg.propertyId : msg;
      if (id == null) return;
      setCentres((prev) => prev.filter((r) => r.propertyId !== id));
    };
    const onStaffDeleted = (msg) => {
      const id = msg && typeof msg === 'object' ? msg.staffId : msg;
      if (id == null) return;
      setCentres((prev) => {
        let changed = false;
        const next = prev.map((row) => {
          if (!row.centreStaff?.some((s) => s.staffId === id)) return row;
          changed = true;
          return { ...row, centreStaff: row.centreStaff.filter((s) => s.staffId !== id) };
        });
        return changed ? next : prev;
      });
    };

    Eventbus.on(E.CENTRE_CREATED, onCentre);
    Eventbus.on(E.CENTRE_UPDATED, onCentre);
    Eventbus.on(E.CENTRE_DELETED, onCentreDeleted);
    Eventbus.on(E.PROPERTY_CREATED, onProperty);
    Eventbus.on(E.PROPERTY_UPDATED, onProperty);
    Eventbus.on(E.PROPERTY_DELETED, onPropertyDeleted);
    Eventbus.on(E.STAFF_CREATED, onStaff);
    Eventbus.on(E.STAFF_UPDATED, onStaff);
    Eventbus.on(E.STAFF_DELETED, onStaffDeleted);
    return () => {
      Eventbus.off(onCentre);
      Eventbus.off(onCentreDeleted);
      Eventbus.off(onProperty);
      Eventbus.off(onPropertyDeleted);
      Eventbus.off(onStaff);
      Eventbus.off(onStaffDeleted);
    };
  }, []);

  const centreOptions = useMemo(() => {
    const grouped = new Map();
    for (const item of centres) {
      const key = item.centreId ?? `no-centre-${item.propertyId}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          label: item.centreDisplayName || item.centreName || 'Unnamed Centre',
          centreId: item.centreId,
          centreAreaName: item.centreAreaName,
          centreRegionName: item.centreRegionName,
          currentStatus: item.currentStatus,
          centreTags: item.centreTags,
          centreStaff: item.centreStaff,
          options: [],
        });
      }
      grouped.get(key).options.push({
        ...item,
        value: item.propertyId ? `property-${item.propertyId}` : `centre-${key}`,
        label: item.propertyId
          ? (item.propertyName || item.address?.address1 || item.buildingTypeName || 'Property')
          : (item.centreName || 'Unnamed Centre'),
      });
    }
    const groups = Array.from(grouped.values());

    if (!hasPropertyAccess) {
      return groups.map((group) => ({
        ...group,
        options: [{
          value: `centre-hidden-${group.centreId ?? group.label}`,
          label: group.label,
          centreId: group.centreId,
          centreName: group.label,
          centreDisplayName: group.label,
          centreAreaName: group.centreAreaName,
          centreRegionName: group.centreRegionName,
          currentStatus: group.currentStatus,
          centreTags: group.centreTags,
          hideOption: true,
          centreStaff: group.centreStaff,
        }],
      }));
    }

    return groups;
  }, [centres]);

  const centreMapItems = useMemo(() => {
    const groupedByCentre = new Map();

    for (const item of centres) {
      if (!item?.centreId) continue;

      const longitude = Number(item.centreAddress?.longitude ?? NaN);
      const latitude = Number(item.centreAddress?.latitude ?? NaN);

      if (!groupedByCentre.has(item.centreId)) {
        groupedByCentre.set(item.centreId, {
          centreId: item.centreId,
          centreName: item.centreName || 'Unnamed Centre',
          centreDisplayName: item.centreDisplayName || item.centreName || 'Unnamed Centre',
          centreAreaName: item.centreAreaName ?? null,
          centreRegionName: item.centreRegionName ?? null,
          currentStatus: item.currentStatus ?? null,
          latitude: Number.isFinite(latitude) ? latitude : null,
          longitude: Number.isFinite(longitude) ? longitude : null,
          properties: [],
        });
      }

      const centre = groupedByCentre.get(item.centreId);

      if (
        centre.latitude == null &&
        Number.isFinite(latitude)
      ) {
        centre.latitude = latitude;
      }

      if (
        centre.longitude == null &&
        Number.isFinite(longitude)
      ) {
        centre.longitude = longitude;
      }

      if (item.propertyId) {
        if (!hasPropertyAccess) continue;
        centre.properties.push({
          propertyId: item.propertyId,
          name: item.address?.address1 || item.buildingTypeName || 'Property',
          city: item.address?.city ?? null,
          postalCode: item.address?.postalCode ?? null,
          status: item.propertyStatusDescription || item.propertyStatusCode || null,
        });
      }
    }

    return Array.from(groupedByCentre.values()).filter(
      (centre) => centre.latitude != null && centre.longitude != null,
    );
  }, [centres, hasPropertyAccess]);

  return {
    centres,
    centreOptions,
    centreMapItems,
    loading,
  };
}
